/**
 * VG 2.0 · Phase 11B — Rete professionale ValutaGenio
 *
 * Public recruitment surface at /professionisti. Consumes existing
 * capabilities (Phase 8B/9/10) — no product-domain change.
 *
 * Guardrails honoured:
 *  - Primary CTA "Proponi la tua candidatura" (§CTA)
 *  - Success factual: "Candidatura ricevuta." (§SUCCESS STATE)
 *  - No pricing/subscription/marketplace/matching (§Forbidden claims)
 *  - Network ≠ Circle (§Domain distinctions)
 *  - Draft privacy notice marked "in revisione legale"
 */
import React from "react";
import { ArrowRight, Check, ChevronRight, ShieldCheck } from "lucide-react";
import Logo from "@/components/landing/Logo";
import Footer from "@/components/landing/Footer";
import {
  submitNetworkApplication,
  PROFESSIONAL_ROLES,
  PRIVACY_NOTICE_VERSION,
} from "@/lib/vg2NetworkApplications";

// ---------- SEO ----------------------------------------------------------

function useLandingSEO() {
  React.useEffect(() => {
    const prev = document.title;
    document.title =
      "Rete professionale ValutaGenio · Proponi la tua candidatura";
    const metas = [
      {
        name: "description",
        content:
          "ValutaGenio consegna al Property Advisor un caso già strutturato. Se sei un professionista che lavora accanto ai proprietari immobiliari, puoi proporre la tua candidatura alla rete professionale ValutaGenio.",
      },
      { property: "og:title", content: "Rete professionale ValutaGenio" },
      {
        property: "og:description",
        content:
          "Proponi la tua candidatura per collaborare a casi immobiliari strutturati.",
      },
      { property: "og:type", content: "website" },
    ];
    const attached = [];
    metas.forEach((m) => {
      const key = m.name ? "name" : "property";
      const val = m.name || m.property;
      let el = document.querySelector(`meta[${key}="${val}"]`);
      let created = false;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(key, val);
        document.head.appendChild(el);
        created = true;
      }
      const prevAttr = el.getAttribute("content");
      el.setAttribute("content", m.content);
      attached.push({ el, prev: prevAttr, created });
    });
    let canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical ? canonical.getAttribute("href") : null;
    let canonicalCreated = false;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
      canonicalCreated = true;
    }
    canonical.setAttribute("href", "/professionisti");
    return () => {
      document.title = prev;
      attached.forEach(({ el, prev: p, created }) => {
        if (created) el.remove();
        else if (p !== null) el.setAttribute("content", p);
      });
      if (canonicalCreated) canonical.remove();
      else if (prevCanonical !== null) canonical.setAttribute("href", prevCanonical);
    };
  }, []);
}

// ---------- atoms --------------------------------------------------------

function Eyebrow({ children }) {
  return (
    <span className="inline-block text-[11px] uppercase tracking-[0.22em] font-semibold text-[#B40101] mb-4">
      {children}
    </span>
  );
}

function H2({ children, id }) {
  return (
    <h2
      id={id}
      className="font-serif text-[#0A0A0A] text-3xl md:text-4xl lg:text-[42px] leading-[1.15] tracking-tight max-w-3xl"
    >
      {children}
    </h2>
  );
}

function Lead({ children }) {
  return (
    <p className="mt-6 text-[#0A0A0A]/80 text-[17px] md:text-[18px] leading-relaxed max-w-2xl">
      {children}
    </p>
  );
}

// ---------- navbar -------------------------------------------------------

function LandingNavbar() {
  return (
    <header
      data-testid="np-landing-navbar"
      className="sticky top-0 z-50 w-full bg-white/95 backdrop-blur-sm border-b border-[#E5E7EB]"
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
        <a href="/" data-testid="np-landing-logo-home" className="flex items-center">
          <Logo size="text-xl md:text-2xl" />
        </a>
        <nav className="flex items-center gap-3 md:gap-6 text-sm">
          <a
            href="#come-funziona"
            data-testid="np-landing-nav-funziona"
            className="hidden md:inline text-[#0A0A0A]/70 hover:text-[#B40101] transition-colors"
          >
            Come funziona
          </a>
          <a
            href="/perche-vg"
            data-testid="np-landing-nav-perche-vg"
            className="hidden md:inline text-[#0A0A0A]/70 hover:text-[#B40101] transition-colors"
          >
            Perché ValutaGenio
          </a>
          <a
            href="#candidatura"
            data-testid="np-landing-nav-cta"
            className="inline-flex items-center gap-2 bg-[#B40101] hover:bg-[#8F0101] text-white font-medium text-sm px-4 py-2 rounded-lg transition-colors"
          >
            <span className="hidden sm:inline">Proponi la tua candidatura</span>
            <span className="sm:hidden">Candidati</span>
          </a>
        </nav>
      </div>
    </header>
  );
}

// ---------- hero ---------------------------------------------------------

function Hero() {
  return (
    <section
      data-testid="np-landing-hero"
      className="relative bg-[#F9F9F9] border-b border-[#E5E7EB]"
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-16 md:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-7">
          <Eyebrow>Rete professionale ValutaGenio</Eyebrow>
          <h1
            data-testid="np-landing-hero-h1"
            className="font-serif text-[#0A0A0A] text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] tracking-tight"
          >
            Il tuo lavoro parte da un caso già strutturato.
          </h1>
          <p className="mt-7 text-[#0A0A0A]/80 text-[17px] md:text-[18px] leading-relaxed max-w-2xl">
            ValutaGenio guida il proprietario dall&apos;obiettivo alla decisione e prepara un piano d&apos;azione con ruoli professionali specifici. Se collabori con proprietari immobiliari, puoi proporre la tua candidatura alla rete professionale.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
            <a
              href="#candidatura"
              data-testid="np-landing-hero-cta"
              className="inline-flex items-center gap-2 bg-[#B40101] hover:bg-[#8F0101] text-white font-semibold text-base px-6 py-3.5 rounded-xl transition-all duration-200 hover:-translate-y-0.5 shadow-[0_10px_30px_rgba(180,1,1,0.28)]"
            >
              Proponi la tua candidatura <ArrowRight className="w-5 h-5" />
            </a>
            <a
              href="#come-funziona"
              data-testid="np-landing-hero-secondary"
              className="inline-flex items-center gap-2 text-[#0A0A0A] hover:text-[#B40101] font-medium text-base px-2 py-2 transition-colors"
            >
              Come funziona <ChevronRight className="w-5 h-5" />
            </a>
          </div>
          <p className="mt-6 text-[13px] text-[#4B5563] max-w-2xl">
            La candidatura non garantisce l&apos;ammissione, l&apos;assegnazione di incarichi o alcuna certificazione professionale.
          </p>
        </div>

        <aside className="lg:col-span-5">
          <div
            data-testid="np-landing-hero-card"
            className="rounded-2xl border border-[#E5E7EB] bg-white shadow-[0_20px_50px_-25px_rgba(0,0,0,0.15)] p-6 md:p-7"
          >
            <div className="text-[11px] uppercase tracking-widest text-[#B40101] font-semibold mb-3">
              Nel caso arriva già
            </div>
            <ul className="space-y-2.5 text-[15px] text-[#0A0A0A]">
              {[
                "Obiettivo del proprietario",
                "Quadro economico orientativo",
                "Decisione esplicita",
                "Piano d'azione con ruoli professionali",
                "Handoff EVA con punti aperti",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-[#B40101] mt-1 shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 pt-4 border-t border-[#E5E7EB] text-[13px] text-[#4B5563]">
              Il Property Advisor sceglie chi coinvolgere. Il Circle è il gruppo di lavoro effettivo di quel Caso.
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}

// ---------- section: perché coinvolgiamo professionisti -----------------

function WhySection() {
  return (
    <section className="bg-white border-b border-[#E5E7EB]">
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-24">
        <Eyebrow>Perché ValutaGenio coinvolge professionisti</Eyebrow>
        <H2>Un caso reale richiede più di una valutazione.</H2>
        <Lead>
          Molte decisioni immobiliari si sbloccano solo quando un ruolo specifico entra nel caso: la conformità, la successione, la fiscalità, la ristrutturazione, la certificazione energetica. ValutaGenio riconosce questi ruoli, non li improvvisa.
        </Lead>
      </div>
    </section>
  );
}

// ---------- section: from case to need -----------------------------------

function FromCaseSection() {
  const items = [
    { k: "OBIETTIVO", v: "Il proprietario chiarisce cosa vuole ottenere." },
    { k: "VALUTAZIONE", v: "Emerge un quadro economico orientativo." },
    { k: "DECISIONE", v: "Vendita, locazione, valorizzazione o mantenimento." },
    { k: "PIANO", v: "Ruoli professionali necessari, azione successiva." },
    { k: "PROPERTY ADVISOR", v: "Interpreta il caso, prepara la call, valuta chi coinvolgere." },
    { k: "PROFESSIONISTA", v: "Entra per il proprio ruolo, con contesto già disponibile." },
  ];
  return (
    <section
      id="come-funziona"
      data-testid="np-landing-como-funziona"
      className="bg-[#F9F9F9] border-b border-[#E5E7EB]"
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-24">
        <Eyebrow>Dal caso strutturato al bisogno professionale</Eyebrow>
        <H2>Entri nel caso solo quando serve, con il contesto già a disposizione.</H2>
        <div className="mt-12 rounded-2xl border border-[#E5E7EB] bg-white p-6 md:p-8">
          <ol className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6">
            {items.map((it, i) => (
              <li key={it.k} className="flex gap-4">
                <div className="w-8 h-8 shrink-0 rounded-full border-2 border-[#B40101] text-[#B40101] font-semibold inline-flex items-center justify-center text-[13px]">
                  {i + 1}
                </div>
                <div>
                  <div className="text-[12px] uppercase tracking-widest text-[#B40101] font-semibold">{it.k}</div>
                  <div className="text-[15px] text-[#0A0A0A] mt-0.5">{it.v}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

// ---------- section: network vs circle -----------------------------------

function NetworkVsCircle() {
  return (
    <section className="bg-white border-b border-[#E5E7EB]">
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-24">
        <Eyebrow>Rete estesa e Project Circle</Eyebrow>
        <H2>Due concetti distinti, non un marketplace.</H2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="rounded-2xl border border-[#E5E7EB] p-7 bg-white">
            <div className="text-[11px] uppercase tracking-widest text-[#4B5563] font-semibold mb-3">
              Rete professionale ValutaGenio
            </div>
            <h3 className="font-semibold text-[#0A0A0A] text-lg mb-2">Un bacino di professionisti conosciuti</h3>
            <p className="text-[#0A0A0A]/80 text-[15px] leading-relaxed">
              L&apos;ammissione avviene per decisione esplicita di ValutaGenio. Nessuna verifica automatica delle credenziali, nessun ranking, nessun punteggio pubblico. Nessuna pagina profilo pubblica.
            </p>
          </div>
          <div className="rounded-2xl border border-[#B40101]/30 p-7 bg-[#F9F9F9]">
            <div className="text-[11px] uppercase tracking-widest text-[#B40101] font-semibold mb-3">
              Project Circle
            </div>
            <h3 className="font-semibold text-[#0A0A0A] text-lg mb-2">Il gruppo di lavoro di un singolo Caso</h3>
            <p className="text-[#0A0A0A]/80 text-[15px] leading-relaxed">
              È il Property Advisor a costruirlo, ruolo per ruolo, in base al piano d&apos;azione del proprietario. L&apos;appartenenza alla rete non implica selezione automatica.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- section: how selection works ---------------------------------

function HowSelectionWorks() {
  const steps = [
    { t: "Il PA legge il piano d'azione", d: "Ogni bisogno professionale è già identificato per ruolo canonico." },
    { t: "Il PA sceglie il candidato", d: "Può attingere alla propria rete o consultare la rete estesa." },
    { t: "Il professionista entra per il suo ruolo", d: "Riceve solo il contesto necessario, senza dati non pertinenti." },
    { t: "Se emerge un fatto materiale", d: "Il Caso può richiedere una rivalutazione: il ruolo può cambiare o essere ridefinito." },
  ];
  return (
    <section className="bg-[#F9F9F9] border-b border-[#E5E7EB]">
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-24">
        <Eyebrow>Come funziona la selezione</Eyebrow>
        <H2>Non è un algoritmo. È una scelta professionale.</H2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          {steps.map((s, i) => (
            <div key={s.t} className="flex gap-4">
              <div className="w-9 h-9 shrink-0 rounded-full border-2 border-[#B40101] text-[#B40101] inline-flex items-center justify-center font-semibold">{i + 1}</div>
              <div>
                <div className="font-semibold text-[#0A0A0A]">{s.t}</div>
                <div className="text-[#0A0A0A]/75 text-[15px] leading-relaxed mt-1">{s.d}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- section: what pro receives / expects -------------------------

function ReceivesExpects() {
  return (
    <section className="bg-white border-b border-[#E5E7EB]">
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div>
          <Eyebrow>Cosa riceve il professionista</Eyebrow>
          <H2>Contesto, non contatti freddi.</H2>
          <ul className="mt-8 space-y-3 text-[15px] text-[#0A0A0A]">
            {[
              "L'accesso alla propria disciplina, solo quando il caso lo richiede.",
              "Contesto già organizzato dal Property Advisor.",
              "Chiarezza sul ruolo atteso e sull'azione successiva.",
              "Nessuna pagina profilo pubblica, nessun ranking, nessuna recensione.",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[#B40101] mt-0.5 shrink-0" /> {t}
              </li>
            ))}
          </ul>
        </div>
        <div>
          <Eyebrow>Cosa ValutaGenio si aspetta</Eyebrow>
          <H2>Serietà, competenza, coerenza col ruolo.</H2>
          <ul className="mt-8 space-y-3 text-[15px] text-[#0A0A0A]">
            {[
              "Rispetto della disciplina professionale e degli obblighi di legge.",
              "Trasparenza verso il proprietario e verso il Property Advisor.",
              "Coerenza tra il ruolo scelto e il proprio ambito di lavoro.",
              "Nessuna promessa che ValutaGenio non ha mai fatto al proprietario.",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[#B40101] mt-0.5 shrink-0" /> {t}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

// ---------- section: who may apply --------------------------------------

function WhoMayApply() {
  return (
    <section className="bg-[#F9F9F9] border-b border-[#E5E7EB]">
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-24">
        <Eyebrow>Chi può candidarsi</Eyebrow>
        <H2>Professionisti che lavorano attorno alla decisione immobiliare.</H2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {PROFESSIONAL_ROLES.filter((r) => r.value !== "PROPERTY_ADVISOR").map((r) => (
            <div
              key={r.value}
              className="rounded-xl border border-[#E5E7EB] bg-white px-5 py-4 text-[15px] text-[#0A0A0A]"
            >
              {r.label}
            </div>
          ))}
        </div>
        <p className="mt-8 text-[13px] text-[#4B5563]">
          Se sei un Property Advisor, la superficie dedicata è <a href="/property-advisor" className="text-[#B40101] font-semibold hover:underline">/property-advisor</a>.
        </p>
      </div>
    </section>
  );
}

// ---------- application form --------------------------------------------

const APPLICATIONS_ENABLED =
  String(
    process.env.REACT_APP_ENABLE_PROFESSIONAL_APPLICATIONS ?? "false",
  ).toLowerCase() === "true";

function ApplicationsPausedNotice() {
  return (
    <div
      data-testid="np-applications-paused-notice"
      className="rounded-2xl border border-[#E5E7EB] bg-white p-6 md:p-8 text-center"
    >
      <p className="text-[15px] md:text-[16px] text-[#0A0A0A] leading-relaxed">
        Le candidature alla rete professionale non sono aperte in questo momento.
      </p>
    </div>
  );
}

function ApplicationForm() {
  const [values, setValues] = React.useState({
    first_name: "",
    last_name: "",
    business_name: "",
    email: "",
    phone: "",
    professional_role: "",
    city: "",
    coverage_areas: "",
    professional_description: "",
    motivation: "",
    website: "",
    registration_body: "",
    registration_number: "",
    privacy_notice_acknowledged: false,
  });
  const [state, setState] = React.useState({ status: "idle", error: null });

  const canSubmit =
    values.first_name.trim() &&
    values.last_name.trim() &&
    values.email.trim() &&
    values.phone.trim() &&
    values.professional_role &&
    values.city.trim() &&
    values.coverage_areas.trim() &&
    values.professional_description.trim().length >= 30 &&
    values.motivation.trim().length >= 20 &&
    values.privacy_notice_acknowledged &&
    state.status !== "submitting";

  const update = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setValues((old) => ({ ...old, [k]: v }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setState({ status: "submitting", error: null });
    try {
      await submitNetworkApplication({
        ...values,
        coverage_areas: values.coverage_areas
          .split(/[,;\n]/)
          .map((s) => s.trim())
          .filter(Boolean),
      });
      setState({ status: "done", error: null });
    } catch (err) {
      const message =
        err?.status === 429
          ? "Troppe richieste dalla tua rete. Riprova più tardi."
          : err?.status === 422
          ? "Alcuni campi non sono validi. Controlla e riprova."
          : "Non è stato possibile inviare la candidatura. Riprova.";
      setState({ status: "error", error: message });
    }
  };

  if (state.status === "done") {
    return (
      <SuccessCard
        onReset={() => {
          setValues({
            first_name: "", last_name: "", business_name: "", email: "",
            phone: "", professional_role: "", city: "", coverage_areas: "",
            professional_description: "", motivation: "", website: "",
            registration_body: "", registration_number: "",
            privacy_notice_acknowledged: false,
          });
          setState({ status: "idle", error: null });
        }}
      />
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="np-application-form"
      className="rounded-2xl border border-[#E5E7EB] bg-white p-6 md:p-8 space-y-5"
      noValidate
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field id="first_name" label="Nome" required value={values.first_name} onChange={update("first_name")} testid="np-form-first-name" />
        <Field id="last_name" label="Cognome" required value={values.last_name} onChange={update("last_name")} testid="np-form-last-name" />
        <Field id="business_name" label="Studio / Attività (opzionale)" value={values.business_name} onChange={update("business_name")} testid="np-form-business" />
        <Field id="email" type="email" label="Email" required value={values.email} onChange={update("email")} testid="np-form-email" />
        <Field id="phone" label="Telefono" required value={values.phone} onChange={update("phone")} testid="np-form-phone" />
        <Select id="professional_role" label="Ruolo professionale" required value={values.professional_role} onChange={update("professional_role")} testid="np-form-role">
          <option value="">Seleziona un ruolo…</option>
          {PROFESSIONAL_ROLES.filter((r) => r.value !== "PROPERTY_ADVISOR").map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </Select>
        <Field id="city" label="Città o area di base" required value={values.city} onChange={update("city")} testid="np-form-city" />
        <Field id="coverage_areas" label="Aree di copertura (separate da virgola)" required value={values.coverage_areas} onChange={update("coverage_areas")} testid="np-form-coverage" />
      </div>

      <TextArea
        id="professional_description"
        label="Breve descrizione professionale"
        hint="Minimo 30 caratteri. Ambiti, esperienza, specializzazione."
        required maxLength={800} rows={4}
        value={values.professional_description}
        onChange={update("professional_description")}
        testid="np-form-description"
      />
      <TextArea
        id="motivation"
        label="Perché vuoi candidarti"
        hint="Minimo 20 caratteri."
        required maxLength={500} rows={3}
        value={values.motivation}
        onChange={update("motivation")}
        testid="np-form-motivation"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field id="website" label="Sito o profilo pubblico (opzionale)" value={values.website} onChange={update("website")} testid="np-form-website" />
        <div className="grid grid-cols-2 gap-3">
          <Field id="registration_body" label="Albo / Ordine (opzionale)" value={values.registration_body} onChange={update("registration_body")} testid="np-form-reg-body" />
          <Field id="registration_number" label="N. iscrizione (opzionale)" value={values.registration_number} onChange={update("registration_number")} testid="np-form-reg-num" />
        </div>
      </div>

      <label className="flex items-start gap-3 pt-2 cursor-pointer" htmlFor="privacy_notice_acknowledged">
        <input
          id="privacy_notice_acknowledged"
          type="checkbox"
          checked={values.privacy_notice_acknowledged}
          onChange={update("privacy_notice_acknowledged")}
          data-testid="np-form-privacy"
          className="mt-1 w-4 h-4 accent-[#B40101]"
          aria-required="true"
        />
        <span className="text-[13px] text-[#0A0A0A]/85 leading-relaxed">
          Dichiaro di aver letto l&apos;<a href="/privacy#candidature-professionali" className="text-[#B40101] font-semibold hover:underline" target="_blank" rel="noopener noreferrer">informativa sul trattamento dei dati personali relativa alla candidatura professionale</a>.
          <span className="block text-[12px] text-[#4B5563] mt-1">
            Versione informativa: <code className="font-mono">{PRIVACY_NOTICE_VERSION}</code> · in revisione legale.
          </span>
        </span>
      </label>

      {state.error && (
        <div
          role="alert"
          data-testid="np-form-error"
          className="text-[14px] text-[#B40101] bg-[#B40101]/5 border border-[#B40101]/30 rounded-lg px-4 py-3"
        >
          {state.error}
        </div>
      )}

      <div className="pt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <button
          type="submit"
          disabled={!canSubmit}
          data-testid="np-form-submit"
          aria-label="Proponi la tua candidatura"
          className="inline-flex items-center gap-2 bg-[#B40101] hover:bg-[#8F0101] disabled:bg-[#B40101]/40 disabled:cursor-not-allowed text-white font-semibold text-base px-6 py-3.5 rounded-xl transition-colors"
        >
          {state.status === "submitting" ? "Invio in corso…" : "Proponi la tua candidatura"}
          {state.status !== "submitting" && <ArrowRight className="w-5 h-5" />}
        </button>
        <p className="text-[12px] text-[#4B5563] max-w-sm">
          Nessun costo, nessun abbonamento. ValutaGenio non garantisce l&apos;ammissione né incarichi.
        </p>
      </div>
    </form>
  );
}

function Field({ id, label, required, testid, ...rest }) {
  return (
    <div>
      <label htmlFor={id} className="block text-[13px] font-medium text-[#0A0A0A] mb-1.5">
        {label}{required && <span className="text-[#B40101]"> *</span>}
      </label>
      <input
        id={id}
        required={required}
        data-testid={testid}
        {...rest}
        className="w-full rounded-xl border border-[#E5E7EB] px-4 py-3 text-[15px] text-[#0A0A0A] placeholder-[#6B7280] focus:outline-none focus:ring-2 focus:ring-[#B40101]/40 focus:border-[#B40101]"
      />
    </div>
  );
}

function Select({ id, label, required, testid, children, ...rest }) {
  return (
    <div>
      <label htmlFor={id} className="block text-[13px] font-medium text-[#0A0A0A] mb-1.5">
        {label}{required && <span className="text-[#B40101]"> *</span>}
      </label>
      <select
        id={id}
        required={required}
        data-testid={testid}
        {...rest}
        className="w-full rounded-xl border border-[#E5E7EB] px-4 py-3 text-[15px] text-[#0A0A0A] bg-white focus:outline-none focus:ring-2 focus:ring-[#B40101]/40 focus:border-[#B40101]"
      >
        {children}
      </select>
    </div>
  );
}

function TextArea({ id, label, hint, required, testid, rows, maxLength, value, onChange }) {
  return (
    <div>
      <label htmlFor={id} className="block text-[13px] font-medium text-[#0A0A0A] mb-1.5">
        {label}{required && <span className="text-[#B40101]"> *</span>}
      </label>
      <textarea
        id={id}
        required={required}
        data-testid={testid}
        rows={rows}
        maxLength={maxLength}
        value={value}
        onChange={onChange}
        className="w-full rounded-xl border border-[#E5E7EB] px-4 py-3 text-[15px] text-[#0A0A0A] placeholder-[#6B7280] focus:outline-none focus:ring-2 focus:ring-[#B40101]/40 focus:border-[#B40101]"
      />
      {hint && <p className="text-[12px] text-[#4B5563] mt-1">{hint}</p>}
    </div>
  );
}

function SuccessCard({ onReset }) {
  return (
    <div
      data-testid="np-form-success"
      className="rounded-2xl border border-[#B40101]/30 bg-white p-8 shadow-[0_20px_50px_-25px_rgba(180,1,1,0.15)]"
      role="status"
      aria-live="polite"
    >
      <div className="w-12 h-12 rounded-full bg-[#B40101]/10 inline-flex items-center justify-center mb-4">
        <Check className="w-6 h-6 text-[#B40101]" />
      </div>
      <h3 className="font-serif text-[#0A0A0A] text-2xl md:text-3xl">Candidatura ricevuta.</h3>
      <p className="mt-3 text-[#0A0A0A]/80 text-[16px] leading-relaxed">
        ValutaGenio esaminerà le informazioni inviate. Se il profilo è coerente con le esigenze attuali della rete, il team potrà contattarti per un approfondimento.
      </p>
      <button
        type="button"
        onClick={onReset}
        data-testid="np-form-reset"
        className="mt-6 text-[14px] text-[#B40101] font-medium hover:underline"
      >
        Invia un&apos;altra candidatura
      </button>
    </div>
  );
}

// ---------- CTA final section --------------------------------------------

function CTASection() {
  return (
    <section
      id="candidatura"
      data-testid="np-landing-candidatura"
      className="bg-[#F9F9F9] border-b border-[#E5E7EB]"
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5">
          <Eyebrow>Proponi la tua candidatura</Eyebrow>
          <H2>Un modulo essenziale. Nessun costo.</H2>
          <Lead>
            Raccogliamo solo le informazioni necessarie per valutare la coerenza del profilo. La candidatura non attiva alcuna registrazione automatica, alcun account, alcun abbonamento.
          </Lead>
          <ul className="mt-8 space-y-2 text-[13px] text-[#0A0A0A]/75">
            <li className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#B40101]" /> I tuoi dati non entrano in campagne marketing.</li>
            <li className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#B40101]" /> Nessuna verifica automatica delle credenziali.</li>
            <li className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#B40101]" /> L&apos;ammissione richiede una decisione esplicita del team.</li>
          </ul>
        </div>
        <div className="lg:col-span-7">
          {APPLICATIONS_ENABLED ? <ApplicationForm /> : <ApplicationsPausedNotice />}
        </div>
      </div>
    </section>
  );
}

// ---------- final expectations note --------------------------------------

function FinalNote() {
  return (
    <section className="bg-white border-b border-[#E5E7EB]">
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-14 md:py-20 max-w-3xl">
        <Eyebrow>Aspettative chiare</Eyebrow>
        <p className="text-[15px] text-[#0A0A0A]/80 leading-relaxed">
          L&apos;invio della candidatura non implica ammissione, non attiva l&apos;iscrizione a un elenco pubblico e non è una certificazione professionale. ValutaGenio può decidere se contattare o meno il professionista in base alle esigenze correnti della rete. Nessun impegno commerciale è previsto in questa fase.
        </p>
      </div>
    </section>
  );
}

// ---------- page --------------------------------------------------------

export default function NetworkApplicationLanding() {
  useLandingSEO();
  return (
    <main
      data-testid="np-landing-page"
      className="bg-white text-[#0A0A0A] antialiased overflow-x-hidden"
    >
      <LandingNavbar />
      <Hero />
      <WhySection />
      <FromCaseSection />
      <NetworkVsCircle />
      <HowSelectionWorks />
      <ReceivesExpects />
      <WhoMayApply />
      <CTASection />
      <FinalNote />
      <Footer />
    </main>
  );
}
